import axios from 'axios';
import { getAuthHeaders, getAuthToken } from '@/lib/auth-utils';

const base = `${process.env.NEXT_PUBLIC_API_BASE_URL}/api`;
export type FundingTotals = { annual_premium: string; successful_payments: string; approved_credit: string; funded_total: string; outstanding_balance: string; excess_funded_amount: string; eligible: boolean };
export type FundingEvent = { id: number; type: string; title: string; status: string; amount: string | null; metadata: Record<string, unknown>; created_at: string };
export type FundingStatus = { quotation_no: string; funding: FundingTotals; events: FundingEvent[] };

const headers = () => ({ ...getAuthHeaders(), Accept: 'application/json' });
const requireAuth = () => { if (!getAuthToken()) throw new Error('Please sign in to manage payments.'); };
export async function getMotorFundingStatus(quotationNo: string): Promise<FundingStatus> {
  requireAuth();
  const response = await axios.get(`${base}/motor-funding/status`, { params: { quotation_no: quotationNo }, headers: headers() });
  return response.data.data;
}
export async function recordCashPayment(quotationNo: string, amount: number) {
  requireAuth();
  return (await axios.post(`${base}/motor-funding/cash`, { quotation_no: quotationNo, amount }, { headers: headers() })).data.data;
}
export async function sendPaymentUploadLink(payload: { quotation_no: string; type: 'cheque' | 'online'; channel: 'sms' | 'email'; phone_number?: string; email?: string }) {
  requireAuth();
  return (await axios.post(`${base}/motor-funding/submissions`, payload, { headers: headers() })).data.data;
}
export async function downloadFundingFile(path: string, filename: string) {
  requireAuth();
  const response = await axios.get(`${base}${path}`, { headers: headers(), responseType: 'blob' });
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement('a'); anchor.href = url; anchor.download = filename; anchor.click(); URL.revokeObjectURL(url);
}
export async function viewFundingFile(path: string) {
  requireAuth();
  // Reserve the tab during the click so the browser does not block it after
  // the authenticated receipt request finishes. Passing `noopener` directly
  // to window.open can make browsers return null while still opening a blank
  // tab, so detach the opener after retaining the window reference instead.
  const popup = window.open('about:blank', '_blank');
  if (!popup) throw new Error('Please allow popups to view this document.');
  popup.opener = null;
  try {
    const response = await axios.get(`${base}${path}`, { headers: headers(), responseType: 'blob' });
    const url = URL.createObjectURL(response.data);
    popup.location.replace(url);
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    popup.close();
    throw error;
  }
}
export function fundingError(error: unknown): string {
  if (axios.isAxiosError(error)) return error.response?.data?.message || Object.values(error.response?.data?.errors || {}).flat().join(' ') || 'Unable to complete the payment action.';
  return error instanceof Error ? error.message : 'Unable to complete the payment action.';
}
